import io from "socket.io-client";
import api from "../services/api";


class SocketWorker {
  constructor(companyId, userId) {
    let sessionToken = api.defaults.headers.Authorization
    if (sessionToken && sessionToken.startsWith("Bearer ")) {
      sessionToken = sessionToken.split(" ")[1];
    }

    if (!SocketWorker.instance) {
      this.companyId = companyId
      this.userId = userId
      this.token = sessionToken
      this.socket = null;
      this.configureSocket();
      this.eventListeners = {}; // Armazena os ouvintes de eventos registrados
      this.heartbeatInterval = null;
      SocketWorker.instance = this;

    } else {
      const instance = SocketWorker.instance;
      if (userId && (instance.userId !== userId || instance.token !== sessionToken)) {
        // Update instance with new credentials
        instance.companyId = companyId;
        instance.userId = userId;
        instance.token = sessionToken;

        // Force reconnection with new token
        if (instance.socket) {
          instance.socket.disconnect();
          instance.socket = null;
        }
        instance.configureSocket();
      }
    }

    return SocketWorker.instance;
  }

  configureSocket() {
    if (!this.token || this.token === "undefined") {
      console.warn("SocketWorker: Token missing, skipping connection.");
      return;
    }

    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this?.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      // transports: ["websocket", "polling", "flashsocket"],
      // pingTimeout: 18000,
      // pingInterval: 18000,
      query: { userId: this.userId, token: this.token }
    });

    this.socket.on("connect", () => {
      // console.log("Conectado ao servidor Socket.IO");

      // [ANTIGRAVITY] FIX: Re-attach listeners when socket reconnects/re-configures
      if (this.eventListeners) {
        Object.keys(this.eventListeners).forEach(event => {
          this.eventListeners[event].forEach(callback => {
            // Remove before adding to avoid duplicates if re-attaching to same instance (though instance is usually new here)
            this.socket.off(event, callback);
            this.socket.on(event, callback);
          });
        });
      }

      this.startHeartbeat();
    });

    // [ANTIGRAVITY] Plan 516/518: Handle authentication errors (expired token)
    this.socket.on("connect_error", async (error) => {
      if (error.message === "unauthorized" || error.data?.message === "token expired" || error.message === "xhr poll error") {
        console.warn("SocketWorker: Connection error or Token Expired. Attempting silent refresh...");
        try {
          const { data } = await api.post("/auth/refresh_token");
          if (data && data.token) {
            this.token = data.token;
            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;

            // Reconnect with new token
            this.socket.io.opts.query.token = this.token;
            this.socket.connect();
          }
        } catch (refreshError) {
          console.error("SocketWorker: Silent refresh failed.", refreshError);
        }
      }
    });

    // [ANTIGRAVITY] Plan 581: Handle custom auth_error event from backend
    this.socket.on("auth_error", async (data) => {
      if (data?.message === "company_mismatch") {
        console.warn("SocketWorker: Company mismatch detected. Forcing page reload to sync session...");
        window.location.reload();
        return;
      }
      console.warn("SocketWorker: Received auth_error (Token Expired). Attempting silent refresh...");
      try {
        const { data: refreshData } = await api.post("/auth/refresh_token");
        if (refreshData && refreshData.token) {
          this.token = refreshData.token;
          localStorage.setItem("token", JSON.stringify(refreshData.token));
          api.defaults.headers.Authorization = `Bearer ${refreshData.token}`;

          // Reconnect with new token
          if (this.socket) {
            this.socket.io.opts.query.token = this.token;
            // Force reconnection to update handshake query
            this.socket.disconnect();
            this.socket.connect();
          }
        }
      } catch (refreshError) {
        console.error("SocketWorker: Silent refresh failed on auth_error.", refreshError);
      }
    });

    // [ANTIGRAVITY] Plan 518: Monitor general disconnection to trigger refresh if token is old
    this.socket.on("disconnect", (reason) => {
      this.stopHeartbeat();
      if (reason === "io server disconnect") {
        // The server forced a disconnect, likely due to expired token (Plan 518)
        console.log("SocketWorker: Server persistent disconnect. Refreshing credentials...");
        this.configureSocket(); // This will pull new token from API defaults
      }
      this.reconnectAfterDelay();
    });
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {

    this.connect();
    if (this.socket) {
      this.socket.on(event, callback);
    }

    // Armazena o ouvinte no objeto de ouvintes
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }

    // [ANTIGRAVITY] FIX: Avoid adding same identical callback twice
    if (!this.eventListeners[event].includes(callback)) {
      this.eventListeners[event].push(callback);
    }
  }

  // Emite um evento
  emit(event, data) {
    this.connect();
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    // console.log(event, callback)
    this.connect();
    if (this.eventListeners[event]) {
      // console.log("Desconectando do servidor Socket.IO:", event, callback);
      if (callback) {
        // Desconecta um ouvinte específico
        if (this.socket) this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      } else {
        // console.log("DELETOU EVENTOS DO SOCKET:", this.eventListeners[event]);

        // Desconecta todos os ouvintes do evento
        if (this.socket) {
          this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
        }
        delete this.eventListeners[event];
      }
      // console.log("EVENTOS DO SOCKET:", this.eventListeners);
    }
  }

  disconnect() {
    this.stopHeartbeat();
    this.socket.disconnect();
    this.socket = null
    this.instance = null
    // console.log("Socket desconectado manualmente");
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        // console.log("SocketWorker: Sending heartbeat...");
        this.socket.emit("heartbeat", {
          userId: this.userId,
          companyId: this.companyId
        });
      }
    }, 15000); // 15 segundos
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.socket || !this.socket.connected) {
        // console.log("Tentando reconectar após desconexão");
        this.connect();
      }
    }, 1000);
  }

  // Garante que o socket esteja conectado
  connect() {
    if (!this.socket) {
      this.configureSocket();
    }
  }

  forceReconnect() {

  }
}

// const instance = (companyId, userId) => new SocketWorker(companyId,userId);
const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
